import React, { useRef, useCallback, useMemo, useEffect, ComponentProps } from 'react'
import { BottomSheetFooter, BottomSheetModal, BottomSheetModalProps } from '@gorhom/bottom-sheet'
import { useGameBottomSheetStore } from './useGameBottomSheetStore'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { isWeb, useMaxWidth } from '@/components/WebWrapper'
import { DS } from '@/design-system'
import { ShowCardsBottomSheetContent } from './ShowCardsBottomSheetContent'
import { BetBottomSheetContent } from './BetBottomSheetContent'
import { gameSheetTopY } from './gameSheetSharedValues'
import { CommandsContextProvider } from '../../new-design/hooks/useCommonCommands'

// ***
// CONVEX bottom sheet orchestration functions are now in packages/convex/src/games/helpers/orchestration.ts
// AI: Legacy is in useBottomModal.tsx for now but will be fully migrated to useGameBottomSheetStore and GameBottomSheet component in the near future
// ***

export const GameBottomSheet = () => {
  const insets = useSafeAreaInsets()
  const maxWidth = useMaxWidth()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const { contentName, contentProps, config, registerSheetRef, handleNativeDismiss, closeSheet } =
    useGameBottomSheetStore()

  // Register the ref with the Zustand store immediately after component mounts
  useEffect(() => {
    registerSheetRef(bottomSheetModalRef)
  }, [registerSheetRef])

  // Use the store action as the *official* onDismiss handler
  const handleDismiss = useCallback(() => {
    handleNativeDismiss() // This cleans up the content in the store
  }, [handleNativeDismiss])

  // Reset the shared sheet position when the game screen (and therefore this
  // sheet) goes away, so a stale value never leaves consumers displaced
  // (module-level shared value survives across screen mounts).
  useEffect(() => {
    return () => {
      gameSheetTopY.value = Number.POSITIVE_INFINITY
    }
  }, [])

  const renderContent = useMemo(() => {
    switch (contentName) {
      case 'Bet':
        return <BetBottomSheetContent {...contentProps} />
      case 'ShowCards':
        return <ShowCardsBottomSheetContent {...contentProps} />
      default:
        return null
    }
  }, [contentName, contentProps])

  const renderFooter = useMemo(() => {
    switch (contentName) {
      default:
        return null
    }
  }, [contentName])

  const defaultConfig = {
    backgroundStyle: { backgroundColor: DS.palette['Brand/Cream'] },
    style: {
      marginHorizontal: isWeb ? 'auto' : undefined,
      maxWidth: maxWidth,
      width: '100%',
      paddingBottom: insets.bottom
    }
  } satisfies Omit<BottomSheetModalProps, 'children'>

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      enableDynamicSizing
      onDismiss={handleDismiss}
      {...defaultConfig}
      {...config}
      // Keep the shared sheet-top position in sync for the post-hand ShowCards
      // drawer, so the advisor speech bubble can pin to and ride on its top
      // edge. Other contents (e.g. Bet) keep the previous covered behavior.
      animatedPosition={contentName === 'ShowCards' ? gameSheetTopY : undefined}
    >
      <CommandsContextProvider context="convex">{renderContent}</CommandsContextProvider>
    </BottomSheetModal>
  )
}

export type BetBottomSheetContentProps = ComponentProps<typeof BetBottomSheetContent>
